"use strict";
/* globals module */
/**
 * @module calendarEventRsvpClient
 * @description  CalendarEventRsvp Client provides an easy way to consume CalendarEventRsvp REST API end-points. In order to obtain needed routes `calendarEventRsvpClient` uses `calendarEventRsvpRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var __1 = require("../");
var CalendarEventRsvpClient = /** @class */ (function () {
    function CalendarEventRsvpClient(calendarEventRsvpBatchClient, calendarEventRsvpRoute, apiClient) {
        this.calendarEventRsvpBatchClient = calendarEventRsvpBatchClient;
        this.calendarEventRsvpRoute = calendarEventRsvpRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(CalendarEventRsvpClient.prototype, "routeDefinition", {
        get: function () {
            return this.calendarEventRsvpRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarEventRsvpClient.prototype, "batch", {
        get: function () {
            return this.calendarEventRsvpBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of CalendarEventRsvp resources matching the given criteria.
     * @method
     * @param options Query resource GetCalendarEventRsvpOptions object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example calendarEventRsvpClient.find({
                    pageNumber: 1,
                    pageSize: 10,
                    orderBy: '<field>',
                    orderDirection: '<asc|desc>',
                    search: '<search-phrase>',
                    ids: <identifiers>,
                    calendarIds: <calendar-ids>,
                    calendarNames: <calendar-names>,
                    invitationTypeIds: <invitation-type-ids>,
                    invitationOnly: <true|false>,
                    statudIds: <status-ids>
                    typeIds: <type-ids>,
                    from: <start-date>,
                    to: <end-date>,
                    registrationCloseFrom: <registration-from-date>,
                    registrationCloseTo: <registration-to-date>
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     */
    CalendarEventRsvpClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the CalendarEventRsvp resource.
     * @method
     * @param id CalendarEventRsvp id which uniquely identifies CalendarEventRsvp resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example calendarEventRsvpClient.get(id)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventRsvpClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.routeDefinition.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create CalendarEventRsvp action has been performed. This action creates a new CalendarEventRsvp resource.
     * @method
     * @param data A CalendarEventRsvp object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create CalendarEventRsvp action has been performed.
     * @example calendarEventRsvpClient.create({
                    InvitationOnly: <true|false>,
                    InvitationType: <calendar-rsvp-invitation-type>,
                    InvitationTypeId: '<invitation-type-id>',
                    Json: '<json>',
                    MaxSlots: <max-slots>,
                    MinSlots: <min-slots>,
                    RegistrationCloseDate: '<registration-close-date>',
                    TotalSlots: <total-slots>
                })
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventRsvpClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(data), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update CalendarEventRsvp action has been performed. This action updates a CalendarEventRsvp resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventRsvpRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEventRsvp);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A CalendarEventRsvp object used to update specified CalendarEventRsvp resource.
     * @returns A promise that is resolved once the update CalendarEventRsvp action has been performed.
     * @example calendarEventRsvp is a resource previously fetched using get action.
                calendarEventRsvp.MinSlots = '<min-slots>';
                calendarEventRsvpClient.update(calendarEventRsvp)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventRsvpClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove a CalendarEventRsvp resource from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `calendarEventRsvpRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(calendarEventRsvp);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An calendarEventRsvp object used to delete specified CalendarEventRsvp resource.
     * @returns A promise that is resolved once the remove action has been performed.
     * @example calendarEventRsvp is a resource previously fetched using get action.
                calendarEventRsvpClient.remove(calendarEventRsvp)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                });
     */
    CalendarEventRsvpClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the purge action has been performed. This action will remove all CalendarEventRsvp resources for the specified CalendarEvent from the system if succesfully completed.
     * @method
     * @param data A CalendarEvent object that will have it's CalendarEventRsvps purged.
     * @example calendarEventRsvpClient.purge(calendarEvent)
                .then(function (data) {
                    // perform success action here
                },
                function (response, status, headers, config) {
                    // perform error handling here
                })
     */
    CalendarEventRsvpClient.prototype.purge = function (data) {
        return this.apiClient.delete(this.routeDefinition.purge(data));
    };
    var _a, _b, _c;
    CalendarEventRsvpClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.CalendarEventRsvpBatchClient)),
        tslib_1.__param(1, inversify_1.inject(__1.TYPES.CalendarEventRsvpRoute)),
        tslib_1.__param(2, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.CalendarEventRsvpBatchClient !== "undefined" && __1.CalendarEventRsvpBatchClient) === "function" ? _a : Object, typeof (_b = typeof __1.CalendarEventRsvpRoute !== "undefined" && __1.CalendarEventRsvpRoute) === "function" ? _b : Object, typeof (_c = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _c : Object])
    ], CalendarEventRsvpClient);
    return CalendarEventRsvpClient;
}());
exports.CalendarEventRsvpClient = CalendarEventRsvpClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
